import React, { useEffect, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import userApi from "../../api/user/user.api";
import bookingApi from "../../api/booking/BookingApi";
import ProcessingBar2 from "../../components/ProcessingBar/ProcessingBar2";

const CustomersCard = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [allCustomers, setAllCustomers] = useState([]);
  const [bookedCustomers, setBookedCustomers] = useState([]);
  const [customersWithNoBookings, setCustomersWithNoBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const [{ response: userRes }, { response: bookingRes }] =
          await Promise.all([
            userApi.getAllUsers(),
            bookingApi.getAllBookings(),
          ]);

        const all = userRes?.data || [];
        const bookings = bookingRes?.data?.booking || [];

        const bookedCustomerIds = new Set(bookings.map((b) => b.user?.id));

        const booked = all.filter((c) => bookedCustomerIds.has(c.id));
        const noBookings = all.filter((c) => !bookedCustomerIds.has(c.id));

        setAllCustomers(all);
        setBookedCustomers(booked);
        setCustomersWithNoBookings(noBookings);
      } catch (error) {
        console.error("Error fetching customers or bookings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const currentList =
    activeTab === "all"
      ? allCustomers
      : activeTab === "booked"
      ? bookedCustomers
      : customersWithNoBookings;

  return (
    <div className="bg-drylight dark:bg-neutral-800 rounded-xl border border-border p-5 shadow w-full">
      <h2 className="font-semibold text-yellowlight">Your Customers</h2>
      <div className="border-b border-textGray my-4"></div>
      {/* Tabs */}
      <div className="flex mb-4 gap-4 text-sm font-medium ">
        <button
          className={`px-3 py-1 rounded ${
            activeTab === "all"
              ? "bg-yellowlight text-yellowdark"
              : "text-gray-400 hover:text-yellowlight"
          }`}
          onClick={() => setActiveTab("all")}
        >
          All ({allCustomers.length})
        </button>
        <button
          className={`px-3 py-1 rounded ${
            activeTab === "booked"
              ? "bg-yellowlight text-yellowdark"
              : "text-gray-400 hover:text-yellowlight"
          }`}
          onClick={() => setActiveTab("booked")}
        >
          Booked ({bookedCustomers.length})
        </button>
        <button
          className={`px-3 py-1 rounded ${
            activeTab === "no-bookings"
              ? "bg-yellowlight text-yellowdark"
              : "text-gray-400 hover:text-yellowlight"
          }`}
          onClick={() => setActiveTab("no-bookings")}
        >
          No Bookings ({customersWithNoBookings.length})
        </button>
      </div>
      <div className="bg-drylight rounded-xl border-t border-border p-5 shadow overflow-y-auto max-h-[400px]">
        {isLoading ? (
          <div className="min-h-[400px] flex items-center justify-center">
            <ProcessingBar2 />
          </div>
        ) : (
          <>
            {/* Customer List */}
            {currentList.length === 0 ? (
              <p className="text-gray-400 text-sm">No customers found.</p>
            ) : (
              currentList.map((member) => (
                <Link
                  to={`/customer/preview/${member.id}`}
                  key={member.id} // ✅ Use unique ID instead of index
                  className="flex justify-between items-center border-b border-border pb-4 mb-4 last:border-none last:pb-0 last:mb-0 hover:bg-hover transition rounded-lg px-2"
                  title={`View ${member.displayName}'s profile`}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={member.photo || "/default-avatar.png"} // ✅ Fallback image
                      alt={member.displayName}
                      className="w-10 h-10 rounded-md object-cover"
                    />
                    <div className="text-xs max-w-[200px]">
                      <h3 className="font-medium text-text truncate">
                        {member.displayName}
                      </h3>
                      <p className="text-gray-400 truncate">{member.phone}</p>
                      <p className="text-gray-400 truncate">
                        {member.username}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-textGray whitespace-nowrap">
                    {new Date(member.updatedAt).toLocaleDateString("en-NZ", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </Link>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomersCard;
